import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { generateInterviewQuestions, generateHiringInsights } from '../../../services/cvAnalysisService';
import { analyzeInterviewFeedback } from '../../../services/interviewAnalysisService';

const AIInsightsPanel = ({ candidate, interviewNotes = [] }) => {
  const [insights, setInsights] = useState(null);
  const [interviewQuestions, setInterviewQuestions] = useState(null);
  const [hiringInsights, setHiringInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const handleGenerateInterviewQuestions = async () => {
    setIsLoading(true);
    try {
      const questions = await generateInterviewQuestions(candidate, candidate?.position);
      setInterviewQuestions(questions);
      setActiveTab('questions');
    } catch (error) {
      console.error('Failed to generate interview questions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateHiringInsights = async () => {
    setIsLoading(true);
    try {
      const insights = await generateHiringInsights(
        candidate,
        interviewNotes || [],
        candidate?.position
      );
      setHiringInsights(insights);
      setActiveTab('hiring');
    } catch (error) {
      console.error('Failed to generate hiring insights:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyzeInterviewFeedback = async (notes) => {
    if (!notes || notes?.length === 0) return;
    
    setIsLoading(true);
    try {
      const combinedNotes = notes?.map(note => 
        `${note?.type} (${note?.date}): ${note?.feedback}`
      )?.join('\n\n');
      
      const analysis = await analyzeInterviewFeedback(
        combinedNotes,
        candidate?.name,
        candidate?.position
      );
      setInsights(analysis);
      setActiveTab('analysis');
    } catch (error) {
      console.error('Failed to analyze interview feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (interviewNotes?.length > 0) {
      handleAnalyzeInterviewFeedback(interviewNotes);
    }
  }, [interviewNotes, candidate]);

  return (
    <div className="bg-card border border-border rounded-lg p-6 card-elevation-base">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Brain" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-foreground">AI Insights</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleGenerateInterviewQuestions}
            disabled={isLoading}
          >
            Generate Questions
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleGenerateHiringInsights}
            disabled={isLoading}
          >
            Hiring Insights
          </Button>
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-4 bg-muted/20 rounded-lg p-1">
        {['overview', 'analysis', 'questions', 'hiring']?.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
              ${activeTab === tab 
                ? 'bg-background text-foreground shadow-sm' 
                : 'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'analysis' && 'Interview Analysis'}
            {tab === 'questions' && 'Interview Questions'}
            {tab === 'hiring' && 'Hiring Decision'}
          </button>
        ))}
      </div>
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Icon name="Loader" size={24} color="var(--color-primary)" className="animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">AI is analyzing...</p>
          </div>
        </div>
      )}
      {/* Overview Tab */}
      {activeTab === 'overview' && !isLoading && (
        <div className="space-y-4">
          <div className="bg-muted/10 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="TrendingUp" size={16} color="var(--color-success)" />
              <h4 className="font-semibold text-foreground">AI Score Breakdown</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Overall Match</span>
                <span className="font-semibold text-foreground">{candidate?.aiScore || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Skills Match</span>
                <span className="font-semibold text-foreground">{candidate?.skillsMatch || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Experience Match</span>
                <span className="font-semibold text-foreground">{candidate?.experienceMatch || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Cultural Fit</span>
                <span className="font-semibold text-foreground">{candidate?.culturalFit || 0}%</span>
              </div>
            </div>
          </div>

          {candidate?.recommendations && (
            <div className="space-y-2">
              <h4 className="font-semibold text-foreground">AI Recommendations</h4>
              {candidate?.recommendations?.map((rec, index) => (
                <div 
                  key={index}
                  className={`
                    p-3 rounded-lg border-l-4 
                    ${rec?.type === 'positive' ? 'bg-success/5 border-success' : 'bg-warning/5 border-warning'}
                  `}
                >
                  <h5 className="font-medium text-sm text-foreground">{rec?.title}</h5>
                  <p className="text-sm text-muted-foreground mt-1">{rec?.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Interview Analysis Tab */}
      {activeTab === 'analysis' && insights && !isLoading && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted/10 rounded-lg">
              <div className="text-2xl font-bold text-foreground">{insights?.overallRating}/5</div>
              <div className="text-sm text-muted-foreground">Overall Rating</div>
            </div>
            <div className="text-center p-3 bg-muted/10 rounded-lg">
              <div className="text-2xl font-bold text-foreground">{insights?.technicalRating}/5</div>
              <div className="text-sm text-muted-foreground">Technical Skills</div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-foreground mb-2">Strengths</h4>
            <ul className="space-y-1">
              {insights?.strengths?.map((strength, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {insights?.concerns?.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground mb-2">Areas of Concern</h4>
              <ul className="space-y-1">
                {insights?.concerns?.map((concern, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{concern}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-muted/10 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">AI Summary</h4>
            <p className="text-sm text-muted-foreground">{insights?.summary}</p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Recommendation</h4>
            <p className="text-sm text-foreground">{insights?.recommendation}</p>
          </div>
        </div>
      )}
      {/* Interview Questions Tab */}
      {activeTab === 'questions' && interviewQuestions && !isLoading && (
        <div className="space-y-6">
          {/* Technical Questions */}
          <div>
            <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Code" size={16} color="var(--color-primary)" />
              <span>Technical Questions</span>
            </h4>
            <div className="space-y-3">
              {interviewQuestions?.technicalQuestions?.map((q, index) => (
                <div key={index} className="border border-border rounded-lg p-3">
                  <p className="text-sm text-foreground font-medium">{q?.question}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">{q?.category}</span>
                    <span className={`
                      text-xs px-2 py-1 rounded
                      ${q?.difficulty === 'Easy' ? 'bg-success/10 text-success' : 
                        q?.difficulty === 'Medium'? 'bg-warning/10 text-warning' : 'bg-destructive/10 text-destructive'}
                    `}>
                      {q?.difficulty}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Behavioral Questions */}
          <div>
            <h4 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
              <Icon name="Users" size={16} color="var(--color-accent)" />
              <span>Behavioral Questions</span>
            </h4>
            <div className="space-y-3">
              {interviewQuestions?.behavioralQuestions?.map((q, index) => (
                <div key={index} className="border border-border rounded-lg p-3">
                  <p className="text-sm text-foreground font-medium">{q?.question}</p>
                  <div className="mt-2">
                    <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded">{q?.category}</span>
                    <p className="text-xs text-muted-foreground mt-1">{q?.purpose}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Hiring Decision Tab */}
      {activeTab === 'hiring' && hiringInsights && !isLoading && (
        <div className="space-y-4">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-foreground">Hiring Recommendation</h4>
              <div className="flex items-center space-x-1">
                <Icon name="Target" size={16} color="var(--color-primary)" />
                <span className="text-sm font-semibold text-primary">{hiringInsights?.confidenceLevel}%</span>
              </div>
            </div>
            <p className="text-sm text-foreground font-medium">{hiringInsights?.hiringRecommendation}</p>
          </div>

          <div className="bg-muted/10 rounded-lg p-4">
            <h4 className="font-semibold text-foreground mb-2">Executive Summary</h4>
            <p className="text-sm text-muted-foreground">{hiringInsights?.executiveSummary}</p>
          </div>

          {hiringInsights?.salaryRecommendation && (
            <div className="bg-success/5 border border-success/20 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-2">Salary Recommendation</h4>
              <p className="text-sm font-semibold text-foreground">{hiringInsights?.salaryRecommendation?.range}</p>
              <p className="text-sm text-muted-foreground mt-1">{hiringInsights?.salaryRecommendation?.justification}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
                <Icon name="TrendingUp" size={16} color="var(--color-success)" />
                <span>Success Predictors</span>
              </h5>
              <ul className="space-y-1">
                {hiringInsights?.successPredictors?.map((predictor, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                    <Icon name="CheckCircle" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                    <span>{predictor}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h5 className="font-semibold text-foreground mb-2 flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                <span>Risk Factors</span>
              </h5>
              <ul className="space-y-1">
                {hiringInsights?.riskFactors?.map((risk, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start space-x-2">
                    <Icon name="AlertCircle" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Empty State */}
      {!isLoading && (
        (activeTab === 'analysis' && !insights) ||
        (activeTab === 'questions' && !interviewQuestions) ||
        (activeTab === 'hiring' && !hiringInsights)
      ) && (
        <div className="text-center py-8">
          <Icon name="Brain" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
          <h4 className="font-semibold text-foreground mb-2">No AI Analysis Available</h4>
          <p className="text-sm text-muted-foreground mb-4">
            {activeTab === 'analysis' && 'No interview feedback to analyze'}
            {activeTab === 'questions' && 'Click "Generate Questions" to create interview questions'}
            {activeTab === 'hiring' && 'Click "Hiring Insights" to get AI-powered hiring recommendations'}
          </p>
        </div>
      )}
    </div>
  );
};

export default AIInsightsPanel;